import { LinkedSession } from '../types/solution-xplr.interface';

// Reverse map: ideation intake type → Redux key information field key.
export const INTAKE_TYPE_TO_FIELD_KEY: Record<string, string> = {
  opportunity_statement_solutionxplr: 'opportunityStatement',
  current_process_solutionxplr: 'currentProcess',
  proposed_enhancements_solutionxplr: 'proposedEnhancements',
  estimated_impact_solutionxplr: 'estimatedImpact',
};

/**
 * Convert a snapshot's `custom_metadata.linked_ideation_intake` array into a
 * field-keyed map of LinkedSession objects used by the Linked Object chip.
 *
 * Intake items don't carry a title, so when a matching live linked session
 * exists (by conversation/intake id) its title, name and diagram are reused;
 * otherwise it falls back to a generic label.
 *
 * Shared by version preview (KeyInformation) and version restore (index).
 */
export function buildLinkedSessionsByField(
  linkedIdeationIntake: any[] | undefined | null,
  liveLinkedSessions?: Record<string, LinkedSession[]>
): Record<string, LinkedSession[]> {
  if (!Array.isArray(linkedIdeationIntake) || linkedIdeationIntake.length === 0) {
    return {};
  }

  const liveSessions = Object.values(liveLinkedSessions || {}).flat() as LinkedSession[];
  const liveSessionLookup = new Map<string, LinkedSession>();
  liveSessions.forEach((session) => {
    if (session?.conversation_id) liveSessionLookup.set(session.conversation_id, session);
    if (session?.id) liveSessionLookup.set(session.id, session);
  });

  const grouped: Record<string, LinkedSession[]> = {};
  linkedIdeationIntake.forEach((intake: any) => {
    const fieldKey = INTAKE_TYPE_TO_FIELD_KEY[intake?.type];
    if (!fieldKey) return;

    const match =
      liveSessionLookup.get(intake?.conversation_id) || liveSessionLookup.get(intake?.id);

    const session: LinkedSession = {
      id: intake?.id,
      use_case_title: match?.use_case_title || match?.session_name || 'Linked Session',
      session_name: match?.session_name || '',
      created_by: intake?.created_by_name || match?.created_by || '',
      created_at: intake?.created_at || '',
      conversation_id: intake?.conversation_id,
      field_type: intake?.type,
      semantic_knowledge: match?.semantic_knowledge || { report: '', file_summary: '' },
      ideation_intake: [intake],
      diagram_content: (match as any)?.diagram_content,
    } as LinkedSession;

    if (!grouped[fieldKey]) grouped[fieldKey] = [];
    grouped[fieldKey].push(session);
  });

  return grouped;
}
